import { LanguageToolClient, LanguageToolClientImpl } from "./client";
import { RulesConfig } from "./types";
import { getStorageItem, setStorageItem } from "../../utils/localStorage";
import { config } from "../../config/corrector";
import rfdc from "rfdc";
import { ConfigProps, Variants } from "../../config/types";

export type LanguageToolProps = {
    server?: string
    client?: LanguageToolClient
}

interface UserConfig {
    variant: Variants
    preferences: Map<string,string|boolean>
}

const globalRulesConfig : RulesConfig = {
    enabledRules: [],
    disabledRules: [ "ESPAI_FI", "ADVERBIS_MENT", "TOO_LONG_SENTENCE", "WHITESPACE_RULE"],
    enabledCategories: [],
    disabledCategories: []
}

// const _emptyRulesConfig = () : RulesConfig => {
//     return {
//         enabledRules: [],
//         disabledRules: [],
//         enabledCategories: [],
//         disabledCategories: []
//     }
// }

export class LanguageToolOptionManager {
    
    private client: LanguageToolClient;
    private variant: Variants

    private preferences: Map<string, string|boolean>

    private userConfig: ConfigProps

    constructor( props? : LanguageToolProps) {
        this.client = props?.client ?? new LanguageToolClientImpl({
            server: props?.server || "https://languagetool.org/api"
          })
        this.variant = Variants.General

        this.preferences = new Map()

        
        const clone = rfdc()
        this.userConfig = clone(config)

        this.loadUserSettings()
    }

    public getConfig() {
        return this.userConfig
    }

    private loadUserSettings() {
        const stored : UserConfig | undefined = getStorageItem<UserConfig | undefined>({key: "corrector", defaultValue: undefined })

        if(!stored) {
            return
        }

        this.variant = stored.variant
        this.preferences = new Map(stored.preferences)

        this.preferences.forEach((value,k) => {
            const [t, key] = k.split('%%%')
            for(let g=0;g<this.userConfig.groups.length;g++) {
                const group = this.userConfig.groups[g]
                for(let o=0;o<group.options.length;o++) {
                    const option = group.options[o]
                    if (option.type === t) {
                        switch(option.type) {
                            case "radio":
                                if(option.id === key) {
                                    for(let v=0;v<option.values.length;v++) {
                                        if (option.values[v].id === value) {
                                            option.values[v].default=true
                                        } else {
                                            option.values[v].default=false
                                        }
                                    }
                                    return
                                }
                                break;
                            case "checkbox":
                                for(let v=0;v<option.values.length;v++) {
                                    if (option.values[v].id === key) {
                                        option.values[v].default = value as boolean
                                        return
                                    }
                                }
                        }
                    }                    
                }
            }
        })
    }

    private saveUserSettings() {
        setStorageItem({key: "corrector", value: {variant: this.variant, preferences: Array.from(this.preferences)}})
        this.loadUserSettings()
    }

    public getLanguageToolResults(text: string) {
        return this.client.getLanguageToolResults(this.getLanguage(), text, this.getRules())
    }

    public setVariant(variant: Variants) {
        this.variant = variant
        this.saveUserSettings()
    }

    public getVariant() {
        return this.variant
    }

    public setPreference(type: "checkbox"|"radio", key: string, value: string, checked: boolean = true) {
        switch(type) {
            case "checkbox":
                this.preferences.set(`checkbox%%%${key}`, checked)
                break;
            case "radio":
                this.preferences.set(`radio%%%${key}`, value)
                break;
        }
        
        
        this.saveUserSettings()
    }

    private getLanguage() {
        switch(this.variant) {
            case Variants.Valencianes:
                return 'ca-ES-valencia'
            case Variants.Balears:
                return 'ca-ES-balear'
            default:
                return 'ca-ES'
        }
    }

    public getRules() : RulesConfig {

        const variantRules = this.getVariantRules()

        const userConfigRules = this.getUserConfigRules()

        return {
            enabledRules: [ ...globalRulesConfig.enabledRules, ...variantRules.enabledRules, ...userConfigRules.enabledRules],
            disabledRules: [...globalRulesConfig.disabledRules, ...variantRules.disabledRules, ...userConfigRules.disabledRules],
            enabledCategories: [ ...globalRulesConfig.enabledCategories, ...variantRules.enabledCategories, ...userConfigRules.enabledCategories],
            disabledCategories: [...globalRulesConfig.disabledCategories, ...variantRules.disabledCategories, ...userConfigRules.disabledCategories]
        }
    }

    private getVariantRules() : RulesConfig {
        return { ...emptyRules, ...config.defaults[this.variant] }
    }

    private getUserConfigRules() : RulesConfig {

        const userConfigRules: RulesConfig = {
            enabledRules: [],
            disabledRules: [],
            enabledCategories: [],
            disabledCategories: []
        }

        for(let g=0;g<this.userConfig.groups.length;g++) {
            
            const group = this.userConfig.groups[g]
            
            if (group.variant && group.variant !== this.variant) {
                continue
            }

            for(let o=0;o<group.options.length;o++) {
                
                const option = group.options[o]
                
                if (option.hide) {
                    continue
                }

                for(let v=0;v<option.values.length;v++) {
                    
                    const value = option.values[v]
                    
                    const checked = value.default;
                    const useRules = value.invertRules ? !checked: checked

                    if (value.hide || !useRules) {
                        continue
                    }
                    
                    if (value.rules?.enabledRules?.length) {
                        userConfigRules.enabledRules = [...userConfigRules.enabledRules, ...value.rules.enabledRules]
                    }
                    
                    if (value.rules?.disabledRules?.length) {
                        userConfigRules.disabledRules = [...userConfigRules.disabledRules, ...value.rules.disabledRules]
                    }
                    
                    if (value.rules?.disabledCategories?.length) {
                        userConfigRules.disabledCategories = [...userConfigRules.disabledCategories, ...value.rules.disabledCategories]
                    }
                    
                    if (value.rules?.enabledCategories?.length) {
                        userConfigRules.enabledCategories = [...userConfigRules.enabledCategories, ...value.rules.enabledCategories]
                    }
                }                    
            }
        }
        return { ...emptyRules, ...userConfigRules }
    }
}

const emptyRules : RulesConfig = {
    enabledRules: [],
    disabledRules: [],
    enabledCategories: [],
    disabledCategories: []
}